import styles from "./styles.scss";
import ChatInput from "../chat-input";
import ChatContents from "../chat-contents";
import {useContext, useEffect, useState} from "preact/hooks";
import {CurrentUserContext, MessagesContext, RoomsContext, UsersStatusContext} from "../../contexts";
import socket, { MessageTypes } from "../../socket";
import {getRoom} from "../../utils";
import {getUserStatus} from "../../selectors";
import ListItem from "../list-item";
import Icon from "../Icon/Icon";
import {isAfter, startOfDay, subDays, parseISO} from "date-fns";
import {fetchRoomDetails, silenceUser} from "../../api";
import ChatInputDropdown from "../ChatInputDropdown/ChatInputDropdown";
import {NavigationContext} from "../../contexts/NavigationContext";

/**
 * @param {String} room? null if this is a direct conversation (since there might not be any rooms created yet)
 * @param {String} contactInternalId The internalId of the contact
 */
const Chat = ({ roomId, contactInternalId, onRequestMoreMessages, broadcastList = [] }) => {
  const rooms = useContext(RoomsContext);
  const messages = useContext(MessagesContext);
  const usersStatus = useContext(UsersStatusContext);
  const currentUser = useContext(CurrentUserContext);
  const { route, navigate } = useContext(NavigationContext);

  const onRequestDetails = roomId => navigate({path: "/chat-details", params: { ...route.params, roomId }});
  const onRequestSearch = query => navigate({path: "/chat-search", params: {...route.params, query}});

  const [silenced, setSilenced] = useState(false);
  const [restricted, setRestricted] = useState(false);
  const [chatInputTop, setChatInputTop] = useState(100);
  const [chatInputContent, setChatInputContent] = useState('');
  const [broadcastsSent] = useState([]);

  const computedRoom = getRoom(rooms, roomId, contactInternalId);
  const displaySenderNames = computedRoom && computedRoom.type !== 'conversation';
  const computedMessages = computedRoom ? filterMessages(messages, computedRoom.id, computedRoom.messageDisplayPeriodDays) : [];
  const floodTimeout = getUserStatus(usersStatus, currentUser.internalId).timeout;
  const [roomDetails, setRoomDetails] = useState(null);
  const restrictedUntil = computedRoom && computedRoom.restrictedUntil;
  const isModerator = currentUser.is_moderator;

  const onSendMessage = content => {
    const timestamp = new Date().toISOString();
    if (broadcastList.length > 0) {
      const message = {
        message: {
          type: 'text',
          content,
          sentAt: timestamp,
          senderLabel: currentUser.name,
        },
        receiverInternalId: "",
      };
      broadcastList.forEach(profile => {
        socket.emit(MessageTypes.SEND_DIRECT_MESSAGE, {message : message.message, receiverInternalId: profile.internal_id});
      });
      broadcastsSent.push({...message.message, senderInternalId: currentUser.internalId});
    } else {
      socket.emit(MessageTypes.SEND_DIRECT_MESSAGE, {
        message: {
          type: 'text',
          content,
          sentAt: timestamp,
          senderLabel: currentUser.name,
        },
        receiverInternalId: contactInternalId,
        roomId: roomId,
      });
    }
  };

  const onRequestSilenceUser = (internalId, isSilenced, timeoutMinutes = 30) => {
    const minutes  = isSilenced ? -1 : timeoutMinutes;
    silenceUser(currentUser.organization, internalId, minutes).then(() => {
      updateRoomDetails(roomId);
    }).catch(err => {
      alert("Ocorreu um erro ao silenciar o usuário. Entre em contato com o suporte.");
      throw new Error(err);
    });
  };

  const updateRoomDetails = (roomId) => {
    return new Promise((resolve, reject) => {
      fetchRoomDetails(roomId)
        .then(response => {
          const details = response.data;
          setRoomDetails(details);
          resolve(details);
        })
        .catch(err => {
          reject(err);
          throw new Error(err);
        });
    });
  };

  useEffect(() => {
    let interval = null;

    if (isModerator && roomId) {
      updateRoomDetails(roomId).then(roomDetails => {
        const now = new Date().toISOString();
        let silencedBefore = roomDetails.members.filter(m => m.timeout && m.timeout > now).length;
        interval = setInterval(() => {
          const now = new Date().toISOString();
          const silencedNow = roomDetails.members.filter(m => m.timeout && m.timeout > now).length;
          if (silencedNow !== silencedBefore)
            updateRoomDetails(roomId);
          silencedBefore = silencedNow;
        }, 1000);
      });
    }

    return () => {
      if (interval)
        clearInterval(interval);
    }
  }, [roomId]);

  useEffect(() => {
    const now = new Date().toISOString();
    setSilenced(floodTimeout && now < floodTimeout);
    setRestricted(restrictedUntil && now < restrictedUntil);
    const interval = setInterval(() => {
      const now = new Date().toISOString();
      setSilenced(floodTimeout && now < floodTimeout);
      setRestricted(restrictedUntil && now < restrictedUntil);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [floodTimeout, restrictedUntil]);

  const onChatInputHeightChanged = height => {
    setChatInputTop(height + 34); // 34 is a manually calculated magic number
  };

  return (
    <div className={styles.container}>
      {isModerator && <div style={{backgroundColor: "#fff"}}>
        <ListItem
        style={{boxShadow: 'rgba(0,0,0,.08) 0 1px 3px 0', zIndex: 10}}
        title="Abrir o painel de moderação"
        fadedSubtitle
        hoverFeedback
        onClick={() => onRequestDetails(roomId)}
        right={<Icon name="arrow_forward" />} /></div>}
      {broadcastList && broadcastList.length > 0 && <ListItem
        style={{boxShadow: 'rgba(0,0,0,.08) 0 1px 3px 0', zIndex: 10}}
        title={broadcastList.map(p => p.name).join(", ")}
      />}
      <ChatContents
        displaySenderNames={displaySenderNames}
        messages={broadcastList && broadcastList.length > 0 ? broadcastsSent : computedMessages}
        onRequestMoreMessages={() => onRequestMoreMessages(roomId)}
        roomDetails={roomDetails}
        onRequestSilenceUser={onRequestSilenceUser}
        internalId={currentUser.internalId}
        onRequestSearch={onRequestSearch}
      />
      <ChatInput
        onSubmit={onSendMessage}
        silenced={silenced}
        restricted={restricted && !isModerator}
        onHeightChanged={onChatInputHeightChanged}
        onContentChanged={value => setChatInputContent(value)}
        showHashtag={computedRoom && computedRoom.type === "channel"}
        showAttachment={(computedRoom && computedRoom.type === "conversation") || isModerator}
      />
      <ChatInputDropdown
        inputContent={chatInputContent}
        bottom={chatInputTop}
        onRequestSearch={onRequestSearch}
      />
    </div>
  );
};

const filterMessages = (messages, roomId, messageDisplayPeriodDays = 0) => {
  let out = messages.filter(message => message.roomId === roomId);
  if (messageDisplayPeriodDays)
    out = out.filter(message => isAfter(parseISO(message.sentAt), startOfDay(subDays(new Date(), messageDisplayPeriodDays - 1))));
  return out;
};

export default Chat;



// WEBPACK FOOTER //
// ./components/chat/index.js